import { useEffect, useState } from "react";

import { Redirect, useParams } from "react-router-dom";

import {
  CHANNEL_DETAIL_INCLUDE,
  CHANNEL_DETAIL_VIEW_ACTION,
  EPISODE_LIST_INCLUDE,
  EpisodeListFilters,
  getChannelDetailData,
  getEpisodeListData,
  patchChannel,
  postChannelAction,
} from "@/api/endpoints/channels";
import { SOURCE } from "@/constants";
import { useMessages } from "@/containers/Messages/MessageContext";
import ChannelContext from "@/contexts/ChannelContext";
import useSearchParams from "@/hooks/useSearchParams";
import { useUser } from "@/hooks/useUser";
import { ROUTES } from "@/routes_new";
import { Channel, ChannelDetailEpisode } from "@/types/Channel";
import Container from "@components/Container";
import Pagination from "@components/Pagination";
import Rhythm from "@components/Rhythm";
import SpinnerAnimated from "@components/SpinnerAnimated";

import Breadcrumbs from "../../Breadcrumbs";
import TranscriptStatusExplainer from "../../TranscriptStatusExplainer";
import ChannelMetadata from "../ChannelMetadata";
import ChannelSettings from "../ChannelSettings";
import ChannelStatistics from "../ChannelStatistics";
import ChannelTitle from "../ChannelTitle";
import EpisodeListTable from "../EpisodeListTable";

import styles from "./ChannelDetailView.module.css";

const ChannelDetailView: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const { data: userData, isLoading: isLoadingUser } = useUser();
  const isStaffUser = userData?.ok ? userData.data.is_staff : false;

  const [searchParams, setSearchParams] = useSearchParams<EpisodeListFilters>();
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_count: 1,
  });
  const { page = 1 } = searchParams;

  const [channel, setChannel] = useState<Channel | undefined>();
  const [episodes, setEpisodes] = useState<ChannelDetailEpisode[]>([]);

  const [loadingChannel, setLoadingChannel] = useState<boolean>(false);
  const [loadingEpisodes, setLoadingEpisodes] = useState<boolean>(false);
  const [transcribingAll, setTranscribingAll] = useState<boolean>(false);
  const [reindexingEpisodes, setReindexingEpisodes] = useState<boolean>(false);
  const { success, danger } = useMessages();

  // Get channel details
  useEffect(() => {
    setLoadingChannel(true);
    getChannelDetailData(id, [
      CHANNEL_DETAIL_INCLUDE.AUTOMATIC_TRANSCRIPTION,
      CHANNEL_DETAIL_INCLUDE.INTERNAL_STATISTICS,
    ])
      .then((response) => {
        if (response.ok && response.data) {
          setChannel(response.data);
        } else {
          throw new Error(response.statusText);
        }
      })
      .finally(() => {
        setLoadingChannel(false);
      });
  }, [id]);

  // Get list of episodes
  useEffect(() => {
    setLoadingEpisodes(true);
    getEpisodeListData(id, searchParams, [
      EPISODE_LIST_INCLUDE.LATEST_TRANSCRIPT_STATUS,
    ])
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.data;
      })
      .then((data) => {
        if (data) {
          const { results, count, page, page_count } = data;

          if (results) {
            setEpisodes(results);
          }

          setPagination({ count, page, page_count });
        }
      })
      .finally(() => {
        setLoadingEpisodes(false);
      });
  }, [id, searchParams]);

  // Handle channel updates
  const updateChannel = (values: Partial<Channel>) => {
    if (!channel?.id) {
      return;
    }
    return patchChannel(channel.id, values).then((response) => {
      if (response.ok && response.data) {
        // If the channel is already set then only update part of the channel, because
        // the response does not contain the same "includes" as in getChannelDetailData.
        setChannel((prevChannel) => {
          if (prevChannel) {
            return { ...prevChannel, ...response.data };
          }
          return response.data;
        });
      } else {
        throw new Error(response.statusText);
      }
    });
  };

  const transcribeAll = () => {
    if (!channel?.id) {
      return;
    }
    setTranscribingAll(true);
    postChannelAction(channel.id, CHANNEL_DETAIL_VIEW_ACTION.TRANSCRIBE)
      .then((response) => {
        if (response.ok && response.data?.status) {
          success({ title: response.data.status });
        } else {
          throw new Error(response.statusText);
        }
      })
      .catch(() => {
        danger({ title: "Failed to start transcription" });
      })
      .finally(() => {
        setTranscribingAll(false);

        setTimeout(() => {
          window.location.reload();
        }, 10000);
      });
  };

  const reindexEpisodes = () => {
    if (!channel?.id) return;

    setReindexingEpisodes(true);

    postChannelAction(channel.id, CHANNEL_DETAIL_VIEW_ACTION.REINDEX_EPISODES)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(response.data?.error);
        }

        return response.data.status;
      })
      .then((message) => {
        if (!message) {
          return;
        }

        success({
          title: message,
        });
      })
      .catch((error) => {
        danger({ title: error.message });
      })
      .finally(() => {
        setReindexingEpisodes(false);
      });
  };

  if (isLoadingUser) return null;
  if (!isStaffUser) return <Redirect to={ROUTES.APP_HOME} />;

  return (
    <ChannelContext.Provider
      value={{ channel: channel, updateChannel: updateChannel }}
    >
      <Container>
        <Rhythm>
          <Breadcrumbs channel_name={channel?.name} />
        </Rhythm>
        <Rhythm>
          <div className={styles.channelDetailsGridContainer}>
            <div className={styles.titleArea}>
              {!loadingChannel && channel && <ChannelTitle />}
            </div>
            <div className={styles.settingsArea}>
              {!loadingChannel && channel && (
                <ChannelSettings
                  onTranscribeAll={transcribeAll}
                  transcribingAll={transcribingAll}
                  onReindexEpisodes={reindexEpisodes}
                  reindexingEpisodes={reindexingEpisodes}
                />
              )}
            </div>
            <div className={styles.statisticsArea}>
              {!loadingChannel && channel && <ChannelStatistics />}
            </div>
            <div className={styles.metadataArea}>
              {!loadingChannel && channel && <ChannelMetadata />}
            </div>
          </div>
        </Rhythm>
        <Rhythm>
          {pagination.count !== null && (
            <p>Displaying {pagination.count} episodes</p>
          )}
        </Rhythm>
        <Rhythm>
          <EpisodeListTable
            channel_type={channel?.channel_type || SOURCE.PODCAST}
            episodes={episodes}
          />

          {loadingEpisodes ? (
            <SpinnerAnimated fill="currentColor" width="2rem" />
          ) : null}
        </Rhythm>
        <Rhythm>
          <Pagination
            formatLabel={({ max, pageNumber }) =>
              `Page ${pageNumber} of ${max}`
            }
            current={page}
            max={pagination.page_count}
            next={() => {
              setSearchParams({
                page: page + 1,
              });
            }}
            previous={() => {
              setSearchParams({
                page: page - 1,
              });
            }}
          />
        </Rhythm>
        <Rhythm>
          <TranscriptStatusExplainer />
        </Rhythm>
      </Container>
    </ChannelContext.Provider>
  );
};

export default ChannelDetailView;
